import React, { useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import styled from "@emotion/styled";
import { zIndex, borderRadius, fontSize, lineHeight, spacing, surface, transition, shadow, dataViz } from "@atlas-int/tokens";

export const primaryColor1 = dataViz.accentBlue;
export const overlayPortalContainerId = "overlay-portal-container";

//#region Styling
const RootBase = styled.span`
  border-bottom-style: dotted;
  border-bottom-width: 2px;
  cursor: help;
  pointer-events: auto;
`;
const StandardRoot = styled(RootBase)`
  border-bottom-color: ${primaryColor1};
`;
const PolicyRoot = styled(RootBase)`
  border-bottom-color: inherit;
`;
const TooltipBase = styled.div`
  position: fixed;
  z-index: ${zIndex.glossaryOverlay};
  width: 18rem;
  opacity: 0;
  transition: opacity ${transition.base};
  background-color: transparent;
`;
const StandardTooltip = styled(TooltipBase)`
  color: inherit;
`;
const PolicyTooltip = styled(TooltipBase)`
  background-color: transparent;
  border-style: none;
`;

const TooltipContent = styled.div`
  background-color: ${surface.white};
  padding: ${spacing[8]};
  box-shadow: ${shadow.sm};
  border-radius: ${borderRadius.base};
  font-size: ${fontSize.base};
  line-height: ${lineHeight.normal};
  text-transform: none;
`;

const LegendContent = styled.div`
  background-color: ${surface.white};
  padding: ${spacing[6]};
  box-shadow: ${shadow.sm};
  border-radius: ${borderRadius.base};
  font-size: ${fontSize.base};
  line-height: ${lineHeight.normal};
`;

export enum TooltipAlign {
  Standard,
  YAxis,
}

export enum TooltipType {
  Standard,
  Policy,
}

interface BaseProps {
  term: React.ReactNode;
  explanation: React.ReactNode;
  citation: React.ReactNode | undefined;
  align: TooltipAlign;
  attachment: React.ReactNode | undefined;
}

type Props = BaseProps &
  (
    | {
        type: TooltipType.Standard;
      }
    | {
        type: TooltipType.Policy;
        color: string;
      }
  );

const GlossaryTerm = (props: Props) => {
  const { term, explanation, citation, type, align, attachment } = props;
  const rootEl = useRef<HTMLDivElement | null>(null);
  const tooltipEl = useRef<HTMLDivElement | null>(null);
  const overlayPortalContainerNodeRef = useRef<HTMLElement | null>(null);

  const [isTooltipShown, setIsTooltipShown] = useState<boolean>(false);

  useLayoutEffect(() => {
    const node = document.querySelector<HTMLElement>(
      `#${overlayPortalContainerId}`,
    );
    overlayPortalContainerNodeRef.current = node;
    const tooltipElm = tooltipEl.current;
    const rootElm = rootEl.current;
    if (tooltipElm !== null && rootElm !== null) {
      const { top, left } = rootElm.getBoundingClientRect();
      const tooltipSpacing = 5;
      const tooltipHeight = tooltipElm.offsetHeight;
      const tooltipWidth = tooltipElm.offsetWidth;
      let tooltipTopValue = top + rootElm.offsetHeight + tooltipSpacing;
      let tooltipLeftValue = left;
      if (tooltipTopValue + tooltipHeight > window.innerHeight) {
        // tooltip will exceed the windows height
        tooltipTopValue = top - tooltipSpacing - tooltipHeight;
      }
      if (left + tooltipWidth > window.innerWidth) {
        // tooltip will exceed the windows width
        tooltipLeftValue = window.innerWidth - tooltipWidth - tooltipSpacing;
      }
      const tooltipColors =
        props.type === TooltipType.Policy
          ? `
          color: ${props.color};
          borderColor: ${props.color};
        `
          : "";
      tooltipElm.style.cssText = `
        left: ${tooltipLeftValue}px;
        top: ${tooltipTopValue}px;
        opacity: 1;
        ${tooltipColors}
      `;
    }
  }, [isTooltipShown]);
  const overlayPortalContainerNode = overlayPortalContainerNodeRef.current;

  const rootStyles: React.CSSProperties =
    align === TooltipAlign.YAxis
      ? {
          borderBottom: "none",
          borderLeft: `2px dotted ${primaryColor1}`,
        }
      : {};
  const Tooltip =
    type === TooltipType.Standard ? StandardTooltip : PolicyTooltip;
  const citationElm = citation !== undefined ? <em>{citation}</em> : null;
  let tooltip: React.ReactPortal | null;
  if (isTooltipShown !== false && overlayPortalContainerNode !== null) {
    tooltip = ReactDOM.createPortal(
      <Tooltip ref={tooltipEl}>
        {attachment ? (
          <LegendContent>
            {explanation} {citationElm}
            {attachment}
          </LegendContent>
        ) : (
          <TooltipContent>
            {explanation} {citationElm}
          </TooltipContent>
        )}
      </Tooltip>,
      overlayPortalContainerNode,
    );
  } else {
    tooltip = null;
  }
  const Root = type === TooltipType.Standard ? StandardRoot : PolicyRoot;
  return (
    <Root
      onMouseEnter={() => setIsTooltipShown(true)}
      onMouseLeave={() => setIsTooltipShown(false)}
      style={rootStyles}
      ref={rootEl}
    >
      {term}
      {tooltip}
    </Root>
  );
};

export default GlossaryTerm;
